import { useEffect, useState } from "react";
import { DATA_CHANGED_EVENT, request } from "./api";
import type { AudienceRule } from "../types";

// The audience rules on each module of a PD day and on every item in them,
// keyed by what they govern, for the "shown only to…" notes on the screen. A
// failed lookup leaves the notes out rather than breaking the page.
export function useAudienceRules(moduleIDs: string[]): Record<string, AudienceRule[]> {
  const [rules, setRules] = useState<Record<string, AudienceRule[]>>({});
  const [reload, setReload] = useState(0);
  const key = [...new Set(moduleIDs)].sort().join(",");

  useEffect(() => {
    const bump = () => setReload((count) => count + 1);
    window.addEventListener(DATA_CHANGED_EVENT, bump);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, bump);
  }, []);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    Promise.all(
      key.split(",").map((id) =>
        request<{ rules: AudienceRule[] }>(`/api/admin/audience-rules?module_id=${id}`)
          .then((data) => data.rules)
          .catch(() => [] as AudienceRule[])
      )
    ).then((lists) => {
      if (cancelled) return;
      const byResource: Record<string, AudienceRule[]> = {};
      for (const rule of lists.flat()) (byResource[rule.resource_id] ||= []).push(rule);
      setRules(byResource);
    });
    return () => {
      cancelled = true;
    };
  }, [key, reload]);

  return rules;
}

// "Only shown to Treatment (Fall 2026)" / "Hidden from Control (Fall 2026)".
export function audienceNote(rules: AudienceRule[] | undefined): string {
  if (!rules?.length) return "";
  const only = rules.filter((rule) => rule.include).map((rule) => rule.label);
  const hidden = rules.filter((rule) => !rule.include).map((rule) => rule.label);
  return [
    only.length ? `Only shown to ${only.join(" and ")}` : "",
    hidden.length ? `Hidden from ${hidden.join(", ")}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}
